/**
 * The client's view of its Ghostfile: an account id, the loadout it will
 * attest, and the last thing the server said about the file. Persisted in
 * localStorage; URL params override for probes (`?account=`, `?loadout=<json>`).
 * The FILE panel (Tab) edits the loadout. Legality is only advisory here —
 * the server refuses illegal loadouts at spawn, it never strips them.
 */
import { ALL_ITEMS, KEYSTONES, LEDGER_ITEMS, MAX_ATTESTED, itemById, ledgerTradeText, type LedgerItem } from "@shared/manifest/items";
import { closeHint, crewButton, graphOpenLine, graphSandboxLine, graphShopLine } from "./hud/keyhint";
import { DEFAULT_LOADOUT, netDelta, validateLoadout, WEAPON_DEPTH, type Loadout, type Ranks } from "@shared/manifest/loadout";
import { BUDGET_PER_PERCENT, ADDITIVE } from "@shared/manifest/stats";
import { xpForDepth, totalXpToReach } from "@shared/progression/depth";
import { WEAPON_LIST, type WeaponId } from "@shared/weapons/manifest";
import type { FileMsg } from "@shared/net/protocol";
import { sandboxAccount, type Account, type GhostRun } from "@shared/progression/account";
import { filesWord, publicIdentity } from "@shared/identity/identity";
import { COSMETICS, canRewrite, slotsOf, slotsWord, cosmeticById } from "@shared/endgame/rewrite";
import { SEASON_PASS_COSMETICS, passThemePalette } from "@shared/economy/catalog";
import type { ContractView } from "@shared/endgame/contracts";
import type { AuditDef, AuditEntry } from "@shared/endgame/audits";
import { CHIPS, chipById, type Socket } from "@shared/manifest/chips";
import { FIRMWARES, firmwareById } from "@shared/manifest/firmwares";
import { CURRICULA, gateFor, MAX_RANK, xpForRank, type Mastery } from "@shared/progression/mastery";
import { redact, STAMPS } from "@shared/progression/stamps";
import { glyphFor, glyphSvg } from "@shared/identity/glyph";
import { counterView, MAX_CAPITAL_PER_UNIT, nameFee, NAME_DEPTH, RUN_DAILY_CAP, RUN_DEPTH } from "@shared/economy/counter";
import { newFileSecret } from "@shared/progression/account";
import { crtPhrase } from "./crt";
import type { CounterClient, CounterView } from "./counter";
import { COUNTER_URL } from "./config";
import { CHAPTERS, chapterFor, MONIKERS, monikerById, unlockedMonikers, wornMoniker } from "@shared/identity/monikers";
import { sanitizeLookCode } from "@shared/identity/look";
import { inCity } from "@shared/net/city";
import { weaponName } from "./hud/kill";
import { unitsLabel } from "./runcue";
import { capitalMark } from "./brand";
import { chitDeskLine } from "@shared/city/chit";

const KEY = "meltdown.file";

export interface FileView {
  account: string;
  depth: number;
  xp: number;
  xpIntoDepth: number;
  xpForNext: number;
  scrip: number;
  wakelight: number;
  salvage: number;
  owned: string[];
  ledger: string[];
  /** Raw loadout the client will send (may be illegal on purpose in probes). */
  loadout: unknown;
  /** Local validation of that loadout against what we know of the file. */
  legal: boolean;
  errors: string[];
  netDelta: number;
  /** "offline" applies now; "linked" applies on the next link. */
  applies: "now" | "next-link";
  mastery: Record<string, { xp: number; rank: number; done: string[]; gate: string | null }>;
  stamps: string[];
  stampCount: number;
  /** the host that serves the ledger shop (null offline: everything is already in the sandbox file) */
  shop: string | null;
  /** identity (Stage 8): what the city calls you, the glyph, the Chapter, monikers earned, the Debt owed */
  identity: { glyph: number; glyphSvg: string; chapter: number; moniker: string | null; monikerText: string | null; display: string; unlocked: string[]; debt: { display: string; kills: number } | null; chapters: number[] };
}

export class GhostFile {
  account: string;
  /** Raw loadout as the client will send it. Kept raw so unknown fields reach the server untouched. */
  raw: Record<string, unknown>;
  depth = 1;
  xp = 0;
  scrip = 0;
  /** chits banked onto the file. The desk names them. They are not Scrip. */
  chits = 0;
  wakelight = 0;
  salvage = 0;
  owned: string[] = [];
  ledger: string[] = [];
  /** Server-admitted loadout (what we actually spawned with online). */
  admitted: Loadout | null = null;
  mastery: Record<string, Mastery> = {};
  stamps: string[] = [];
  /** Ledger shop base URL (derived from the room URL); null offline. */
  shop: string | null = null;
  /** equipped moniker id (sent at link; the server wears it only if earned) */
  moniker: string | null = null;
  /** the look (Stage 689): body, build, coat, shoulder as one code; sent at link, worn by everyone's renderer */
  look = 0;
  /** told when the look changes, so the local body can be recut */
  onLook: ((code: number) => void) | null = null;
  /** identity as the server last described it (null offline: derived from the sandbox file) */
  serverIdentity: FileMsg["identity"] | null = null;
  onIdentity: ((f: GhostFile) => void) | null = null;
  onChange: ((f: GhostFile) => void) | null = null;
  onStamp: ((lines: string[], ranks: string[], challenges: string[]) => void) | null = null;
  private panel: HTMLElement | null = null;
  private graph: HTMLElement | null = null;
  private open = false;
  private graphOpen = false;
  private busy = "";

  /** the file's credential, kept beside the id in localStorage and sent with every change */
  secret = "";

  /** the touch build (Stage 145): read from the HUD root, the one element both sides agree on */
  private get touchHud(): boolean {
    return typeof document !== "undefined" && !!document.getElementById("hud")?.classList.contains("touch");
  }

  constructor(private online: () => boolean) {
    const q = new URLSearchParams(location.search);
    let stored: { account?: string; secret?: string; loadout?: Record<string, unknown>; moniker?: string | null; look?: number } = {};
    try {
      stored = JSON.parse(localStorage.getItem(KEY) ?? "{}");
    } catch {
      stored = {};
    }
    this.account = q.get("account") ?? stored.account ?? `blank:${Math.random().toString(36).slice(2, 10)}`;
    // The file's own credential (Stage 26). Generated once, kept beside the id, sent with every
    // request that changes the file — because the id is published on the prize board and used to be
    // enough on its own to Rewrite someone back to Depth 1.
    this.secret = q.get("secret") ?? stored.secret ?? newFileSecret();
    this.moniker = q.get("moniker") ?? stored.moniker ?? null;
    this.look = sanitizeLookCode(q.has("look") ? Number(q.get("look")) : stored.look);
    const urlLoadout = q.get("loadout");
    if (urlLoadout) {
      try {
        this.raw = JSON.parse(urlLoadout);
      } catch {
        this.raw = { ...DEFAULT_LOADOUT };
      }
    } else this.raw = stored.loadout ?? { ...DEFAULT_LOADOUT, attested: [] };
    // offline the sandbox file is the reference: everything owned, Depth 50, mastery 30
    const sb = sandboxAccount(this.account);
    this.depth = sb.depth;
    this.xp = sb.xp;
    this.scrip = sb.wallet.scrip;
    this.owned = sb.owned.slice();
    this.mastery = sb.mastery;
    const net = q.get("net");
    if (net) {
      try {
        const u = new URL(net);
        this.shop = `${u.protocol === "wss:" ? "https:" : "http:"}//${u.host}`;
      } catch {
        this.shop = null;
      }
    }
    // offline in the hub: `?shop=<host>` loads the real file from the ledger host (trophies, ghosts, identity)
    const shop = q.get("shop");
    // the city (Stage 692) is networked, but the room's File message carries no campaign save: the
    // desk needs the whole file, so the city fetches it from the ledger host as the offline hub does
    if (shop && (!net || inCity(q))) {
      this.shop = shop;
      void this.load().then(() => this.loadEndgame());
    } else if (this.shop) void this.loadEndgame();
    // the counter-ledger client is not built here: see ensureCounter() (Stage 48)
    this.persist();
  }

  private counterLoading: Promise<CounterClient | null> | null = null;
  /**
   * The chain client is its own chunk (Stage 48): viem and the contract ABIs are a quarter of the
   * bundle's source and a player needs none of it to wake, walk and shoot. It loads the first time
   * the ledger is opened, asked about or acted on — never on the way to the first frame — and a
   * file with no shop (offline) never loads it at all.
   *
   * `fallback` is a host to use when the file has no shop: the WALLET page on the title menu, which
   * has no room and no `?shop=`, passes the built counter-ledger host. One client either way, so a
   * wallet connected on that page is the wallet the FILE page's Counter-Ledger sees.
   */
  ensureCounter(fallback: string | null = null): Promise<CounterClient | null> {
    if (this.counter) return Promise.resolve(this.counter);
    const shop = this.shop ?? fallback;
    if (!shop) return Promise.resolve(null);
    if (!this.counterLoading) {
      this.counterLoading = import("./counter")
        .then(({ CounterClient }) => {
          // in production the counter-ledger is its own Worker (VITE_COUNTER_URL); in development it is the same host
          const c = new CounterClient(COUNTER_URL ?? shop, this.account, (rec, view) => {
            if (this.accountRecord) this.accountRecord.counter = rec;
            this.counterState = view;
            this.render();
            this.onIdentity?.(this);
            this.onCounter?.();
          });
          c.secret = this.secret;
          c.onChange = () => {
            this.render();
            this.onCounter?.();
          };
          this.counter = c;
          void c.load();
          this.render();
          this.onCounter?.();
          return c;
        })
        .catch(() => {
          // a transient failure must not become a session-long "no ledger": forget the attempt so the next open retries
          this.counterLoading = null;
          return null;
        });
    }
    return this.counterLoading;
  }

  /** run an action against the counter once its chunk is in; a file with no shop does nothing */
  private withCounter(fn: (c: CounterClient) => unknown): void {
    void this.ensureCounter().then((c) => c && fn(c));
  }

  /** the file's range ghosts as last loaded from the host */
  ghosts: Record<string, GhostRun> = {};
  loaded = false;
  /** the whole account as the host last described it (the campaign reads its save from here) */
  accountRecord: Account | null = null;
  /** endgame (Stage 11): today's board, the week's Audit and leaderboard, the Deep Wake — from the ledger host */
  endgame: { day: number; contracts: ContractView[]; audit: (AuditDef & { week: number }) | null; board: AuditEntry[]; season: Parameters<import("./hud/hud").Hud["setSeason"]>[0] } = { day: 0, contracts: [], audit: null, board: [], season: null };
  onEndgame: ((f: GhostFile) => void) | null = null;
  /** the counter-ledger client (Stage 11b); null offline */
  counter: CounterClient | null = null;
  /** anything else drawn from the counter-ledger client (the WALLET page) redraws on this */
  onCounter: (() => void) | null = null;
  /** the counter record as the panel sees it (from the account record; refreshed by every counter op) */
  counterState: CounterView | null = null;
  /** the last private room this file opened (Stage 20): the code is the access control, so it is shown, not the URL */
  privateCode: string | null = null;
  onJoinAudit: (() => void) | null = null;
  /** which section Tab opens on: the game answers "market" from a safe zone's kiosk */
  openSection: () => "top" | "market" = () => "top";
  /** which panel is up: the name desk is not the market */
  panelSection: "top" | "market" | "name" = "top";

  /** Fetch the endgame board (host-wide) and the file's daily progress. */
  async loadEndgame(): Promise<boolean> {
    if (!this.shop) return false;
    try {
      const [eg, daily] = await Promise.all([fetch(`${this.shop}/endgame`).then((r) => r.json()), fetch(`${this.shop}/file/${encodeURIComponent(this.account)}/daily`).then((r) => r.json())]);
      const e = eg as { day: number; audit: AuditDef & { week: number }; board: AuditEntry[]; season: GhostFile["endgame"]["season"] };
      const d = daily as { day: number; contracts: ContractView[] };
      this.endgame = { day: d.day, contracts: d.contracts, audit: e.audit, board: e.board, season: e.season };
      this.render();
      this.onEndgame?.(this);
      return true;
    } catch {
      return false;
    }
  }

  /** claim / rewrite / cosmetic ops on the ledger host; the answer carries the account and today's board */
  async postEndgame(op: "claim" | "rewrite" | "cosmetic", body: Record<string, unknown>): Promise<{ ok: boolean; reason?: string }> {
    if (!this.shop) return { ok: false, reason: "NO LEDGER HOST LINKED" };
    try {
      const res = await fetch(`${this.shop}/file/${encodeURIComponent(this.account)}/${op}`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ ...body, secret: this.secret }) });
      const r = (await res.json()) as { ok: boolean; reason?: string; account?: Account; daily?: { day: number; contracts: ContractView[] } };
      if (r.account) this.applyAccount(r.account);
      if (r.daily) {
        this.endgame.contracts = r.daily.contracts;
        this.endgame.day = r.daily.day;
      }
      this.render();
      this.onEndgame?.(this);
      return { ok: r.ok, reason: r.reason };
    } catch (e) {
      return { ok: false, reason: String(e) };
    }
  }

  /** Apply a saved preset's raw loadout (validated like any loadout). */
  loadPreset(slot: number): boolean {
    const p = this.accountRecord?.presets?.[slot - 1];
    if (!p || !p.loadout || typeof p.loadout !== "object") return false;
    this.setRaw({ ...(p.loadout as Record<string, unknown>) });
    return true;
  }
  onLoaded: ((f: GhostFile) => void) | null = null;

  /** Fetch the file from the ledger host and apply it (offline hub). */
  async load(): Promise<boolean> {
    if (!this.shop) return false;
    try {
      const res = await fetch(`${this.shop}/file/${encodeURIComponent(this.account)}`);
      const a = (await res.json()) as Account | null;
      if (!a || !a.id) return false;
      this.applyAccount(a);
      return true;
    } catch {
      return false;
    }
  }

  /** Apply a whole account record (from the ledger host): the same fields a File message carries, identity derived here. */
  applyAccount(a: Account): void {
    this.accountRecord = a;
    this.counterState = counterView(a);
    this.depth = a.depth;
    this.xp = a.xp;
    this.scrip = a.wallet.scrip;
    this.chits = a.chits ?? 0;
    this.wakelight = a.wallet.wakelight;
    this.salvage = a.wallet.salvage;
    this.owned = a.owned.slice();
    this.ledger = a.ledger.slice();
    this.stamps = a.stamps.slice();
    this.ghosts = { ...a.ghosts };
    for (const [w, m] of Object.entries(a.mastery)) this.mastery[w] = { xp: m.xp, rank: m.rank, done: m.done.slice(), counters: { ...m.counters } };
    const pi = publicIdentity(a, a.name);
    this.serverIdentity = { glyph: pi.glyph, chapter: pi.chapter, moniker: pi.moniker, display: pi.display, unlocked: unlockedMonikers(a).map((m) => m.id), debt: a.debt ? { display: a.debt.display, glyph: 0, kills: a.debt.kills } : null, chapters: a.chapters.slice() };
    const first = !this.loaded;
    this.loaded = true;
    this.render();
    this.onIdentity?.(this);
    this.onChange?.(this);
    if (first) this.onLoaded?.(this);
  }

  /** The campaign endpoint on the ledger host: faction, completions, worn protocols. */
  async postCampaign(body: Record<string, unknown>): Promise<{ ok: boolean; reason?: string; account?: Account }> {
    if (!this.shop) return { ok: false, reason: "NO LEDGER HOST LINKED" };
    try {
      const res = await fetch(`${this.shop}/file/${encodeURIComponent(this.account)}/campaign`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ ...body, secret: this.secret }) });
      const r = (await res.json()) as { ok: boolean; reason?: string; account?: Account };
      return r;
    } catch (e) {
      return { ok: false, reason: String(e) };
    }
  }

  /** Post a range ghost to the file (best effort). */
  async postGhost(run: GhostRun): Promise<boolean> {
    if (!this.shop) return false;
    try {
      const res = await fetch(`${this.shop}/file/${encodeURIComponent(this.account)}/ghost`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ run, secret: this.secret }) });
      const r = (await res.json()) as { ok: boolean };
      return r.ok;
    } catch {
      return false;
    }
  }

  ranks(): Ranks {
    const out: Ranks = {};
    for (const [w, m] of Object.entries(this.mastery)) out[w as WeaponId] = m.rank;
    return out;
  }

  loadoutJson(): string {
    return JSON.stringify(this.raw);
  }

  /** Identity claims sent at link: the equipped moniker (the server decides whether it was earned) and the look (every look is free). */
  identityJson(): string {
    return JSON.stringify({ moniker: this.moniker, look: this.look });
  }

  /** wear a look (Stage 689): kept with the file on this device, sent at the next link */
  setLook(code: number): void {
    const next = sanitizeLookCode(code);
    if (next === this.look) return;
    this.look = next;
    this.persist();
    this.onLook?.(next);
  }

  setMoniker(id: string | null): void {
    this.moniker = id && monikerById(id) ? id : null;
    this.persist();
    this.render();
    this.onIdentity?.(this);
    this.onChange?.(this);
  }

  /** The identity view: server-described online; offline derived from the sandbox file (Depth 50 → NAMED). */
  identityView(): FileView["identity"] {
    const si = this.serverIdentity;
    if (si) {
      const g = glyphFor(this.account, si.chapter >= 3 ? 50 : si.chapter >= 2 ? 25 : si.chapter >= 1 ? 10 : 1);
      g.seed = si.glyph;
      return { glyph: si.glyph, glyphSvg: glyphSvg(g, 22, "#35f2ff"), chapter: si.chapter, moniker: si.moniker, monikerText: monikerById(si.moniker)?.text ?? null, display: si.display, unlocked: si.unlocked.slice(), debt: si.debt ? { display: si.debt.display, kills: si.debt.kills } : null, chapters: si.chapters.slice() };
    }
    const sb = sandboxAccount(this.account);
    sb.moniker = this.moniker;
    const worn = wornMoniker(sb, this.moniker);
    const g = glyphFor(this.account, sb.depth);
    return { glyph: g.seed, glyphSvg: glyphSvg(g, 22, "#35f2ff"), chapter: chapterFor(sb.depth), moniker: worn?.id ?? null, monikerText: worn?.text ?? null, display: chapterFor(sb.depth) >= 3 ? "BLANK" : (worn?.text ?? "BLANK"), unlocked: unlockedMonikers(sb).map((m) => m.id), debt: null, chapters: [1, 2, 3] };
  }

  /** The loadout as the sim should apply it locally (validated against what we know). */
  localLoadout(): Loadout {
    return validateLoadout(this.raw, this.owned, this.depth, this.ranks()).loadout;
  }

  applyServer(f: FileMsg): void {
    this.account = f.account;
    this.depth = f.depth;
    this.xp = f.xp;
    this.scrip = f.scrip;
    this.chits = f.chits ?? this.chits;
    this.wakelight = f.wakelight;
    this.salvage = f.salvage;
    this.owned = f.owned.slice();
    if (f.reason === "join") this.admitted = f.loadout as Loadout;
    if (f.mastery) {
      for (const [w, m] of Object.entries(f.mastery)) this.mastery[w] = { xp: m.xp, rank: m.rank, done: m.done.slice(), counters: { ...m.counters } };
    }
    if (f.stamps) this.stamps = f.stamps.slice();
    if (f.identity) {
      this.serverIdentity = f.identity;
      // the server wears what was earned: mirror it so the panel and the next link agree
      if (f.identity.moniker !== this.moniker) {
        this.moniker = f.identity.moniker;
        this.persist();
      }
      this.onIdentity?.(this);
    }
    if (f.ledger.length) this.ledger.push(...f.ledger);
    if ((f.newStamps?.length || f.ranks?.length || f.challenges?.length) && f.reason === "stamp") {
      this.onStamp?.(f.newStamps?.map((id) => STAMPS.find((s) => s.id === id)?.line ?? id) ?? [], f.ranks ?? [], f.challenges ?? []);
    }
    this.render();
    this.onChange?.(this);
  }

  /** Buy a node through the ledger shop (Scrip); the host answers with the whole file. */
  async buy(nodeId: string, refund = false): Promise<{ ok: boolean; reason?: string }> {
    if (!this.shop) return { ok: false, reason: "OFFLINE: THE SANDBOX FILE ALREADY OWNS EVERYTHING" };
    this.busy = nodeId;
    this.renderGraph();
    try {
      const res = await fetch(`${this.shop}/file/${encodeURIComponent(this.account)}/${refund ? "refund" : "buy"}`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ node: nodeId, secret: this.secret }) });
      const r = (await res.json()) as { ok: boolean; reason?: string; account?: { owned: string[]; wallet: { scrip: number; wakelight: number; salvage: number }; depth: number; xp: number; ledger: string[] } };
      if (r.account) {
        this.owned = r.account.owned.slice();
        this.scrip = r.account.wallet.scrip;
        this.wakelight = r.account.wallet.wakelight;
        this.salvage = r.account.wallet.salvage;
        this.depth = r.account.depth;
        this.xp = r.account.xp;
        const tail = r.account.ledger.slice(-1);
        if (tail.length && this.ledger[this.ledger.length - 1] !== tail[0]) this.ledger.push(...tail);
      }
      this.onChange?.(this);
      return { ok: r.ok, reason: r.reason };
    } catch (e) {
      return { ok: false, reason: String(e) };
    } finally {
      this.busy = "";
      this.renderGraph();
      this.render();
    }
  }

  setChip(weapon: WeaponId, socket: Socket, id: string | null): void {
    const chips = { ...((this.raw.chips as Record<string, Record<string, string | null>>) ?? {}) };
    const slots = { ...(chips[weapon] ?? {}) };
    if (id) slots[socket] = id;
    else delete slots[socket];
    chips[weapon] = slots;
    this.setRaw({ ...this.raw, chips });
  }

  setFirmware(weapon: WeaponId, id: string | null): void {
    const fw = { ...((this.raw.firmware as Record<string, string>) ?? {}) };
    if (id) fw[weapon] = id;
    else delete fw[weapon];
    this.setRaw({ ...this.raw, firmware: fw });
  }

  view(): FileView {
    const v = validateLoadout(this.raw, this.owned, this.depth, this.ranks());
    const mastery: FileView["mastery"] = {};
    for (const w of WEAPON_LIST) {
      const m = this.mastery[w.id];
      if (m) mastery[w.id] = { xp: m.xp, rank: m.rank, done: m.done.slice(), gate: gateFor(w.id, m)?.text ?? null };
    }
    return {
      identity: this.identityView(),
      mastery,
      stamps: this.stamps.slice(),
      stampCount: STAMPS.length,
      shop: this.shop,
      account: this.account,
      depth: this.depth,
      xp: this.xp,
      xpIntoDepth: this.xp - totalXpToReach(this.depth),
      xpForNext: xpForDepth(this.depth),
      scrip: this.scrip,
      wakelight: this.wakelight,
      salvage: this.salvage,
      owned: this.owned.slice(),
      ledger: this.ledger.slice(-12),
      loadout: JSON.parse(JSON.stringify(this.raw)),
      legal: v.ok,
      errors: v.errors.map((e) => `${e.rule}: ${e.detail}`),
      netDelta: netDelta(v.loadout),
      applies: this.online() ? "next-link" : "now",
    };
  }

  setRaw(raw: Record<string, unknown>): void {
    this.raw = raw;
    this.persist();
    this.render();
    this.renderGraph();
    this.onChange?.(this);
  }

  toggleAttest(id: string): void {
    const list = Array.isArray(this.raw.attested) ? (this.raw.attested as string[]).slice() : [];
    const i = list.indexOf(id);
    if (i >= 0) list.splice(i, 1);
    else list.push(id);
    this.setRaw({ ...this.raw, attested: list });
  }

  setKeystone(id: string | null): void {
    this.setRaw({ ...this.raw, keystone: this.raw.keystone === id ? null : id });
  }

  cycleWeapon(which: "primary" | "secondary"): void {
    const cur = this.raw[which] as WeaponId;
    const i = WEAPON_LIST.findIndex((w) => w.id === cur);
    const next = WEAPON_LIST[(i + 1) % WEAPON_LIST.length]!.id;
    this.setRaw({ ...this.raw, [which]: next });
  }

  private persist(): void {
    try {
      localStorage.setItem(KEY, JSON.stringify({ account: this.account, secret: this.secret, loadout: this.raw, moniker: this.moniker, look: this.look }));
    } catch {
      /* private mode */
    }
  }

  // ---- FILE panel ----

  mount(root: HTMLElement): void {
    const el = document.createElement("div");
    el.className = "file";
    el.hidden = true;
    root.appendChild(el);
    this.panel = el;
    el.addEventListener("click", (e) => {
      const t = (e.target as HTMLElement).closest("[data-act]") as HTMLElement | null;
      if (!t) return;
      const act = t.dataset.act!;
      const id = t.dataset.id ?? "";
      if (act === "attest") this.toggleAttest(id);
      else if (act === "keystone") this.setKeystone(id);
      else if (act === "primary" || act === "secondary") this.cycleWeapon(act);
      else if (act === "close") this.toggle(false);
      else if (act === "claim") void this.postEndgame("claim", { id });
      else if (act === "rewrite") void this.postEndgame("rewrite", {});
      else if (act === "buyCosmetic") void this.postEndgame("cosmetic", { op: "buy", id });
      else if (act === "theme") void this.postEndgame("cosmetic", { op: "theme", id: this.accountRecord?.theme === id ? null : id });
      else if (act === "savePreset") void this.postEndgame("cosmetic", { op: "preset", slot: Number(id), name: `PRESET ${id}`, loadout: this.raw });
      else if (act === "loadPreset") this.loadPreset(Number(id));
      else if (act === "setAlias") {
        const input = el.querySelector<HTMLInputElement>(`input[data-alias="${id}"]`);
        void this.postEndgame("cosmetic", { op: "alias", slot: Number(id), alias: input?.value ?? "" });
      } else if (act === "joinAudit") this.onJoinAudit?.();
      else if (act === "link") this.withCounter((c) => c.link());
      else if (act === "buyListing") this.withCounter((c) => c.buy(Number(id)));
      else if (act === "wear") this.withCounter((c) => c.op("wear", { token: Number(id) }));
      else if (act === "reconcile") this.withCounter((c) => c.op("reconcile"));
      else if (act === "payout") this.withCounter((c) => c.op("payout"));
      else if (act === "buyseason") this.withCounter((c) => c.buySeason());
      else if (act === "buyroom") this.withCounter((c) => c.buyRoomHours(1));
      else if (act === "openroom") {
        this.withCounter((c) =>
          c.openRoom(1).then((r) => {
            if (r.ok && r.code) this.privateCode = r.code;
            this.render();
          }),
        );
      }
      else if (act === "prizes") this.withCounter((c) => c.op("prizes"));
      else if (act === "claimPrize") this.withCounter((c) => c.op("claimPrize", { epoch: Number(id) }));
      else if (act === "sell") {
        const price = Number(window.prompt("LIST FOR HOW MUCH $CAPITAL?", "50") ?? 0);
        if (price > 0) this.withCounter((c) => c.sell(Number(id), price));
      }
      else if (act === "attestStamps") this.withCounter((c) => c.op("stamps"));
      else if (act === "registerName") {
        const input = el.querySelector<HTMLInputElement>("input[data-name]");
        this.withCounter((c) => c.registerName(input?.value ?? ""));
      } else if (act === "burnchits") {
        const price = Number(window.prompt("BURN HOW MUCH $CAPITAL?", "1") ?? 0);
        this.withCounter((c) => c.burnForChits(price));
      }
    });
    el.addEventListener("change", (e) => {
      const t = e.target as HTMLSelectElement;
      if (t.dataset.chip) this.setChip(t.dataset.chip as WeaponId, t.dataset.socket as Socket, t.value || null);
      else if (t.dataset.fw) this.setFirmware(t.dataset.fw as WeaponId, t.value || null);
      else if (t.dataset.moniker !== undefined) this.setMoniker(t.value || null);
    });
    document.addEventListener("keydown", (e) => {
      if (e.code === "Tab") {
        e.preventDefault();
        this.toggle(undefined, this.openSection());
      }
      if (e.code === "KeyN" && !(e.target instanceof HTMLInputElement) && !(e.target instanceof HTMLTextAreaElement)) {
        if (this.open && this.panelSection === "name") this.toggle(false);
        else this.toggle(true, "name");
      }
      if (e.code === "KeyG") this.toggleGraph();
      if (e.code === "Escape") {
        if (this.open) this.toggle(false);
        if (this.graphOpen) this.toggleGraph(false);
      }
    });
    const g = document.createElement("div");
    g.className = "graph";
    g.hidden = true;
    root.appendChild(g);
    this.graph = g;
    g.addEventListener("click", (e) => {
      const t = (e.target as HTMLElement).closest("[data-act]") as HTMLElement | null;
      if (!t) return;
      const act = t.dataset.act!;
      const id = t.dataset.id ?? "";
      if (act === "attest") this.toggleAttest(id);
      else if (act === "keystone") this.setKeystone(id);
      else if (act === "buy") void this.buy(id);
      else if (act === "refund") void this.buy(id, true);
      else if (act === "closeGraph") this.toggleGraph(false);
    });
    this.render();
  }

  /** The name row. Empty until the other book can see the wallet. A burn, never a stat. */
  private nameLine(): string {
    const v = this.counterState;
    if (!v?.linked) return "";
    if (v.name) return `NAME <b class="ye">${v.name}</b> <span class="dim">WRITTEN WHERE THEY CAN'T REDACT IT</span>`;
    if (v.nameOpen) return `NAME <input data-name="1" maxlength="24" placeholder="3–24 · A-Z 0-9 _ -"> <span class="btn" data-act="registerName">${crewButton("WRITE IT", this.touchHud)}</span> <span class="dim">${nameFee(3)}–${nameFee(12)} $CAPITAL BY LENGTH, BURNED</span>`;
    return `NAME <span class="dim">THE REGISTRY OPENS AT DEPTH ${NAME_DEPTH}</span>`;
  }

  /** The name desk: the name, and nothing the market sells. */
  nameDeskHtml(): string {
    const name = this.nameLine();
    return `<div class="hd">▲ NAME DESK <span class="x" data-act="close">${closeHint("N", this.touchHud)}</span></div>
      <div class="ln dim">A NAME IS BURNED. IT DOES NOT BUY DAMAGE. IT DOES NOT BUY ARMOR. IT DOES NOT BUY A NODE.</div>
      <div class="ln">${name || `<span class="dim">LINK A WALLET. THE DESK WRITES NOTHING UNTIL THE OTHER BOOK CAN SEE YOU.</span>`}</div>`;
  }

  /** COUNTER-LEDGER // $CAPITAL: the wallet link, the Ghostfile, the stamps on chain, the name, the rig and the market. Identity and ownership only. */
  counterHtml(): string {
    const c = this.counter;
    if (!c) return this.shop ? `<div class="dim">COUNTER-LEDGER // FETCHING THE CHAIN CLIENT…</div>` : "";
    const v = this.counterState;
    const info = c.info;
    const wallet = c.address ? `WALLET <b>${c.short()}</b>` : `<span class="btn" data-act="link">${crewButton("LINK A WALLET", this.touchHud)}</span> <span class="dim">ROBINHOOD WALLET · WALLETCONNECT · INJECTED</span>`;
    const linked = v?.linked ? `LINKED <b>${v.address!.slice(0, 6)}…${v.address!.slice(-4)}</b> · GHOSTFILE <b>${v.ghostfile ? "#" + v.ghostfile : "—"}</b> · STAMPS ON CHAIN <b>${v.stamps}</b>/${this.stamps.length} ${this.stamps.length > v.stamps ? `<span class="btn" data-act="attestStamps">${crewButton("ATTEST", this.touchHud)}</span>` : ""} · $CAPITAL <b>${Number(v.capital).toFixed(0)}</b> <span class="btn" data-act="reconcile">${crewButton("RECONCILE", this.touchHud)}</span>` : c.address ? `<span class="btn" data-act="link">${crewButton("SIGN THE LINK", this.touchHud)}</span> <span class="dim">ONE SIWE STATEMENT; THE GHOSTFILE MINTS WITH SPONSORED GAS</span>` : "";
    const name = this.nameLine();
    const rig = v?.linked ? `RIG ${v.rig.length ? v.rig.map((r) => `<span class="btn ${r.worn ? "on" : ""}" data-act="wear" data-id="${r.worn ? 0 : r.token}">${crewButton(`${r.name}${r.worn ? " · WORN" : ""}`, this.touchHud)}</span> <span class="btn" data-act="sell" data-id="${r.token}">${crewButton("SELL", this.touchHud)}</span>`).join(" ") : "<span class='dim'>NOTHING ON THE RIG YET</span>"}` : "";
    const market = (info?.listings ?? []).map((l) => {
      const s = v?.skins.find((k) => k.token === l.token);
      const mine = !!c.address && l.seller.toLowerCase() === c.address.toLowerCase();
      return `<div class="cos ${s?.owned ? "owned" : ""}"><b>${s?.name ?? "TOKEN " + l.token}</b> <span class="dim">${s?.line ?? ""}</span> · <span class="sw" style="background:${s?.tint ?? "#fff"}"></span> · ${l.amount} LISTED BY ${mine ? "<span class='ye'>YOU</span>" : l.seller.slice(0, 6) + "…"} · <span class="btn ${v?.linked && !c.busy && !mine ? "" : "off"}" data-act="buyListing" data-id="${l.listing}">${crewButton(`${l.price} $CAPITAL`, this.touchHud)}</span></div>`;
    }).join("");
    const run = v?.run ?? { day: 0, banked: 0, owed: 0, paid: 0 };
    // units, not $CAPITAL: the day's rate is set when the day settles, and a unit is worth at most one
    // the sinks (Stage 19): both burn 100%, both sell cosmetics or a server — never a number the sim reads
    const season = this.counter?.info?.season ?? 0;
    const prices = this.counter?.info?.sinks;
    const held = (v?.seasons ?? []).includes(season);
    const hours = v?.roomHours ?? 0;
    const sinkBlock = v?.linked && prices
      ? `<div class="ln">DEEP WAKE SEASON ${season} · ${held ? "<b>BOUGHT OUT</b>" : `<span class="btn" data-act="buyseason">${crewButton(`BUY OUT · ${prices.seasonPass} $CAPITAL`, this.touchHud)}</span>`} · ROOM-HOURS <b>${hours}</b> <span class="btn" data-act="buyroom">${crewButton(`+1 · ${prices.roomHour} $CAPITAL`, this.touchHud)}</span>${hours > 0 ? ` <span class="btn" data-act="openroom">${crewButton("OPEN A PRIVATE ROOM", this.touchHud)}</span>` : ""}<span class="dim"> · BOTH BURNED IN FULL; A PASS IS COSMETICS, AN HOUR IS A SERVER OF YOUR OWN — A PRIVATE ROOM BANKS SCRIP, NEVER $CAPITAL</span>${this.privateCode ? `<br><span class="ye">INVITE CODE <b>${this.privateCode}</b></span> <span class="dim">GIVE IT TO WHOEVER YOU WANT IN; IT IS THE ONLY WAY IN</span>` : ""}</div>`
      : "";
    const runBlock = v?.linked ? `<div class="ln">THE RUN · TODAY <b>${run.banked}</b>/${RUN_DAILY_CAP} · OWED <b>${run.owed}</b> ${unitsLabel(run.owed)} · PAID ${Number(run.paid).toFixed(2)} $CAPITAL ${run.owed > 0 ? `<span class="btn" data-act="payout">${crewButton("WITHDRAW TO WALLET", this.touchHud)}</span>` : ""}${v.runGate ? "" : ` <span class="dim">· BELOW DEPTH ${RUN_DEPTH} THE RUN PAYS SCRIP</span>`}<span class="dim"> · UNITS SETTLE NIGHTLY AT UP TO ${MAX_CAPITAL_PER_UNIT} $CAPITAL EACH, OUT OF THE DAY'S EMISSION; ONCE A DAY HAS SETTLED ITS UNITS ARE A PRIZE BELOW, NOT A WITHDRAWAL</span></div>` : `<div class="ln dim">THE RUN PAYS THE WALLET: LINK ONE AND THE UNITS YOU BANK AT A GATE SETTLE INTO $CAPITAL.</div>`;
    const chainLive = !!(info?.chainId && info.rpc && info.contracts?.capital);
    const chitBlock = `<div class="ln"><img class="cap-mark" src="/icons/chit-64.png" alt="" width="64" height="64"> ${chitDeskLine(this.chits, this.depth, chainLive)} <span class="btn" data-act="burnchits">${crewButton("BURN $CAPITAL FOR CHITS", this.touchHud)}</span></div>`;
    const prizes = c.prizes;
    const prizeBlock = v?.linked ? `<div class="ln">PRIZES ${prizes.length ? prizes.map((p) => `<span class="${p.claimed ? "dim" : ""}">${p.reason} · ${Number(p.amount).toFixed(0)} $CAPITAL ${p.claimed ? "· CLAIMED" : `<span class="btn" data-act="claimPrize" data-id="${p.epoch}">${crewButton("CLAIM", this.touchHud)}</span>`}</span>`).join(" · ") : `<span class="dim">NONE POSTED FOR THIS WALLET</span>`} <span class="btn" data-act="prizes">${crewButton("REFRESH", this.touchHud)}</span> <span class="dim">THE RUN SETTLES NIGHTLY, AUDIT PLACEMENTS WEEKLY, DEEP WAKE CONTRIBUTIONS AT SEASON END; CLAIMS ARE SPONSORED</span></div>` : "";
    const t = info?.treasury;
    const delta = t ? `SUPPLY ${Number(t.supply).toLocaleString()} · BURNED <b>${Number(t.burned).toFixed(0)}</b> · MARKET VOLUME ${Number(t.volume).toFixed(0)} · <span class="gr">NET DELTA: 0.000 — RECONCILED</span>` : (info?.reason ? crtPhrase(info.reason) : "LOADING…");
    return `<div class="sh">${capitalMark()}COUNTER-LEDGER // $CAPITAL <span class="dim">${info ? (info.devnet ? "DEVNET" : "ROBINHOOD CHAIN") + " · CHAIN " + info.chainId : ""}</span></div>
      <div class="ln dim">VANTAGE PRICED YOU. THIS IS THE OTHER BOOK. IT DOES NOT BUY DAMAGE. IT DOES NOT BUY ARMOR. IT DOES NOT BUY A NODE.</div>
      <div class="ln">${wallet}${linked ? " · " + linked : ""}</div>
      ${name ? `<div class="ln">${name}</div>` : ""}${rig ? `<div class="ln">${rig}</div>` : ""}
      ${runBlock}
      ${chitBlock}
      ${sinkBlock}
      ${prizeBlock}
      <div class="sh">LEDGER MARKET · SETTLES ONLY IN $CAPITAL · 5% FEE: 2% BURNED, 2% TREASURY, 1% CREATOR</div>${market || "<div class='dim'>NO LISTINGS</div>"}
      <div class="ln dim">${delta}</div>
      ${c.last ? `<div class="ln am">${c.last}</div>` : ""}`;
  }

  /** Daily contracts, the week's Audit and its board, Rewrite, the Wakelight shop, presets and aliases. */
  endgameHtml(): string {
    const eg = this.endgame;
    const a = this.accountRecord;
    if (!this.shop) return `<div class="sh">ENDGAME</div><div class="dim">CONTRACTS, AUDITS, THE DEEP WAKE AND REWRITE NEED A LEDGER HOST (LINK A ROOM OR OPEN WITH ?SHOP=)</div>`;
    const contracts = eg.contracts.map((c) => `<div class="ct ${c.done ? "done" : ""} ${c.claimed ? "claimed" : ""}"><span>${c.text} <span class="dim">+${c.scrip}¢ +${c.wakelight}◆</span></span><span><span class="bar"><i style="width:${Math.round((100 * c.progress) / c.need)}%"></i></span> ${c.progress}/${c.need} ${c.claimed ? "CLAIMED" : c.done ? `<span class="btn" data-act="claim" data-id="${c.id}">${crewButton("CLAIM", this.touchHud)}</span>` : ""}</span></div>`).join("") || "<div class='dim'>LOADING THE BOARD…</div>";
    const au = eg.audit;
    const me = this.account;
    const board = eg.board.slice(0, 10).map((e, i) => `<div class="${e.account === me ? "me" : ""}"><span>${String(i + 1).padStart(2, "0")} ${e.display}</span><span>${e.score}</span></div>`).join("") || "<div class='dim'>NO SCORES YET THIS WEEK</div>";
    const myAudit = a?.audits && au && a.audits.week === au.week ? `BEST ${a.audits.best} · ${a.audits.played} PLAYED` : "NOT PLAYED YET";
    const audit = au ? `<div class="ln"><b>${au.name}</b> · WEEK ${au.week} · ${au.line}${au.weapons.length ? ` · <span class="dim">${au.weapons.map(weaponName).join(", ")}</span>` : ""} · <span class="btn" data-act="joinAudit">${crewButton("JOIN THE AUDIT", this.touchHud)}</span> <span class="dim">${myAudit}</span></div><div class="board">${board}</div>` : "<div class='dim'>LOADING…</div>";
    const rw = a ? canRewrite(a) : { ok: false, reason: "NO FILE" };
    const rewriteBox = `<div class="rew">REWRITE · ${a?.rewrites ?? 0} SO FAR · ${rw.ok ? `<span class="btn" data-act="rewrite">${crewButton("BURN THE FILE — KEEP THE STAMPS AND THE GLYPH'S AGE — +500 WAKELIGHT", this.touchHud)}</span>` : `<span class="dim">${rw.reason}</span>`}</div>`;
    const slots = a ? slotsOf(a) : { aliases: 1, presets: 1 };
    const owned = a?.cosmetics ?? [];
    const shop = COSMETICS.map((c) => `<div class="cos ${owned.includes(c.id) ? "owned" : ""}"><b>${c.name}</b> <span class="dim">${c.line}</span> · ${owned.includes(c.id) ? (c.kind === "theme" ? `<span class="btn" data-act="theme" data-id="${c.id}">${crewButton(a?.theme === c.id ? "WORN" : "WEAR", this.touchHud)}</span>` : "OWNED") : `<span class="btn ${(a?.wallet.wakelight ?? 0) >= c.wakelight ? "" : "off"}" data-act="buyCosmetic" data-id="${c.id}">${crewButton(`${c.wakelight}◆`, this.touchHud)}</span>`}</div>`).join("");
    const passShop = SEASON_PASS_COSMETICS.filter((c) => owned.includes(c.id)).map((c) => `<div class="cos owned"><b>${c.name}</b> <span class="dim">${c.line}</span> · ${c.kind === "theme" ? `<span class="btn" data-act="theme" data-id="${c.id}">${crewButton(a?.theme === c.id ? "WORN" : "WEAR", this.touchHud)}</span>` : "OWNED"}</div>`).join("");
    const presets = Array.from({ length: slots.presets }, (_, i) => {
      const p = a?.presets?.[i];
      return `<div class="ct"><span>PRESET ${i + 1} · ${p ? p.name : "<span class='dim'>EMPTY</span>"}</span><span>${p ? `<span class="btn" data-act="loadPreset" data-id="${i + 1}">${crewButton("LOAD", this.touchHud)}</span> ` : ""}<span class="btn" data-act="savePreset" data-id="${i + 1}">${crewButton("SAVE CURRENT", this.touchHud)}</span></span></div>`;
    }).join("");
    const aliases = Array.from({ length: slots.aliases }, (_, i) => `<div class="ct"><span>ALIAS ${i + 1} · ${a?.aliases?.[i] ?? "<span class='dim'>EMPTY</span>"}</span><span><input type="text" maxlength="16" data-alias="${i + 1}" placeholder="A NAME THE CITY MAY CALL YOU"> <span class="btn" data-act="setAlias" data-id="${i + 1}">${crewButton("SET", this.touchHud)}</span></span></div>`).join("");
    return `<div class="sh">DAILY CONTRACTS · DAY ${eg.day}</div>${contracts}
      <div class="sh">AUDIT · THE WEEK'S PLAYLIST · LEADERBOARD</div>${audit}
      <div class="sh">REWRITE · WAKELIGHT ${a?.wallet.wakelight ?? this.wakelight}◆</div>${rewriteBox}
      <div class="cols"><div><div class="sh">WAKELIGHT SHOP · THEMES · SLOTS</div>${shop}${passShop}</div><div><div class="sh">PRESETS · ${slotsWord(slots.presets)}</div>${presets}<div class="sh">ALIASES · ${slotsWord(slots.aliases)}</div>${aliases}</div></div>`;
  }

  /** the theme the file wears (palette for the HUD) */
  themePalette(): { cy: string; gr: string; mg: string; ye: string; am: string } | null {
    const id = this.accountRecord?.theme;
    return id ? cosmeticById(id)?.palette ?? passThemePalette(id) : null;
  }

  get isGraphOpen(): boolean {
    return this.graphOpen;
  }

  toggleGraph(on = !this.graphOpen): void {
    this.graphOpen = on;
    if (this.graph) this.graph.hidden = !on;
    if (on) {
      document.exitPointerLock?.();
      this.renderGraph();
    }
  }

  /** The Ledger Graph as a forged district map: three rings of hexes, owned in green, leased in violet. */
  renderGraph(): void {
    if (!this.graph || !this.graphOpen) return;
    const attested = Array.isArray(this.raw.attested) ? (this.raw.attested as string[]) : [];
    const keystone = typeof this.raw.keystone === "string" ? this.raw.keystone : null;
    const W = 720;
    const H = 560;
    const cx = W / 2;
    const cy = H / 2 + 6;
    const pos = new Map<string, { x: number; y: number }>();
    const radii = [92, 172, 252];
    for (const r of [1, 2, 3]) {
      const ring = LEDGER_ITEMS.filter((n) => n.ring === r);
      ring.forEach((n, k) => {
        const a = (k / ring.length) * Math.PI * 2 - Math.PI / 2;
        pos.set(n.id, { x: cx + Math.cos(a) * radii[r - 1]!, y: cy + Math.sin(a) * radii[r - 1]! });
      });
    }
    KEYSTONES.forEach((k, i) => pos.set(k.id, { x: cx + Math.cos(((i / 3) * Math.PI * 2) - Math.PI / 2 + 0.5) * 40, y: cy + Math.sin(((i / 3) * Math.PI * 2) - Math.PI / 2 + 0.5) * 40 }));
    const hex = (x: number, y: number, r: number) => Array.from({ length: 6 }, (_, i) => `${(x + r * Math.cos((i * Math.PI) / 3 + Math.PI / 6)).toFixed(1)},${(y + r * Math.sin((i * Math.PI) / 3 + Math.PI / 6)).toFixed(1)}`).join(" ");
    let links = "";
    const drawn = new Set<string>();
    for (const n of ALL_ITEMS) {
      const a = pos.get(n.id)!;
      for (const l of n.links) {
        const key = [n.id, l].sort().join("+");
        if (drawn.has(key)) continue;
        drawn.add(key);
        const b = pos.get(l);
        if (!b) continue;
        const lit = (attested.includes(n.id) || keystone === n.id) && (attested.includes(l) || keystone === l);
        links += `<line x1="${a.x}" y1="${a.y}" x2="${b.x}" y2="${b.y}" class="${lit ? "lit" : this.owned.includes(n.id) && this.owned.includes(l) ? "own" : ""}"/>`;
      }
    }
    let nodes = "";
    for (const n of ALL_ITEMS) {
      const p = pos.get(n.id)!;
      const owned = this.owned.includes(n.id);
      const on = n.kind === "node" ? attested.includes(n.id) : keystone === n.id;
      const gated = n.requiresDepth > this.depth;
      const cls = `${n.kind} ${owned ? "own" : gated ? "gated" : "lease"} ${on ? "on" : ""} ${this.busy === n.id ? "busy" : ""}`;
      const act = owned ? (n.kind === "node" ? "attest" : "keystone") : "buy";
      nodes += `<g class="${cls}" data-act="${act}" data-id="${n.id}"><polygon points="${hex(p.x, p.y, n.kind === "keystone" ? 20 : 15)}"/><text x="${p.x}" y="${p.y + 3}">${n.kind === "keystone" ? "◆" : owned ? (on ? "▣" : "▢") : gated ? "D" + n.requiresDepth : n.cost}</text><title>${n.name} · ${n.line}${owned ? "" : ` · ${n.cost} SCRIP · NEEDS DEPTH ${n.requiresDepth}`}</title></g>`;
    }
    const ownedCount = this.owned.filter((id) => itemById(id)?.kind === "node").length;
    const v = validateLoadout(this.raw, this.owned, this.depth, this.ranks());
    this.graph.innerHTML = `
      <div class="hd">▲ LEDGER GRAPH · <span class="cy">${ownedCount}/48 OWNED</span> · ATTESTED ${attested.length}/${MAX_ATTESTED} · SCRIP <b>${this.scrip}</b> · DEPTH <b>${String(this.depth).padStart(2, "0")}</b> <span class="x" data-act="closeGraph">${closeHint("G", this.touchHud)}</span></div>
      <svg viewBox="0 0 ${W} ${H}" width="${W}" height="${H}">
        <circle cx="${cx}" cy="${cy}" r="${radii[0]}" class="ring"/><circle cx="${cx}" cy="${cy}" r="${radii[1]}" class="ring"/><circle cx="${cx}" cy="${cy}" r="${radii[2]}" class="ring"/>
        <text x="${cx}" y="${cy - radii[0]! - 24}" class="lbl">RING I · DEPTH 1–4</text><text x="${cx}" y="${cy - radii[1]! - 24}" class="lbl">RING II · DEPTH 6–14</text><text x="${cx}" y="${cy - radii[2]! - 16}" class="lbl">RING III · DEPTH 16–30</text>
        ${links}${nodes}
      </svg>
      <div class="ft ${v.ok ? "" : "bad"}">${this.shop ? graphShopLine(this.touchHud) : graphSandboxLine(this.touchHud)} · ${v.ok ? "ATTESTATION LEGAL" : "ILLEGAL: " + v.errors.map((e) => e.detail).join("; ")}</div>`;
  }

  get isOpen(): boolean {
    return this.open;
  }

  toggle(on = !this.open, section: "top" | "market" | "name" = "top"): void {
    this.open = on;
    if (on) this.panelSection = section;
    if (on) void this.ensureCounter(); // opening the ledger is the first moment the chain client is wanted
    if (this.panel) this.panel.hidden = !on;
    if (on) document.exitPointerLock?.();
    if (on) this.render();
    // from a safe zone's kiosk the panel opens on the market
    if (on && section === "market") this.panel?.querySelector(".cl")?.scrollIntoView();
  }

  render(): void {
    if (!this.panel || !this.open) return;
    if (this.panelSection === "name") {
      this.panel.innerHTML = this.nameDeskHtml();
      return;
    }
    const v = this.view();
    const attested = Array.isArray(this.raw.attested) ? (this.raw.attested as string[]) : [];
    const keystone = typeof this.raw.keystone === "string" ? this.raw.keystone : null;
    const row = (it: LedgerItem) => {
      const on = it.kind === "node" ? attested.includes(it.id) : keystone === it.id;
      const owned = this.owned.includes(it.id);
      const gated = it.requiresDepth > this.depth;
      const w = it.benefits.reduce((a, m) => a + Math.abs(m.delta) * (ADDITIVE.has(m.stat) ? 1 : 100) * BUDGET_PER_PERCENT[m.stat], 0).toFixed(1);
      return `<div class="it ${on ? "on" : ""} ${owned ? "" : "locked"}" data-act="${it.kind === "node" ? "attest" : "keystone"}" data-id="${it.id}">
        <span class="chk">${on ? "▣" : "▢"}</span><span class="nm">${it.name}</span><span class="ring">R${it.ring} · D${it.requiresDepth} · ${it.cost}¢ · ${w}</span>
        <div class="tr">${ledgerTradeText(it)}</div>
        ${gated ? `<div class="c">NEEDS DEPTH ${it.requiresDepth}</div>` : ""}${!owned && !gated ? `<div class="c">NOT IN YOUR FILE</div>` : ""}
      </div>`;
    };
    const wname = (id: unknown) => weaponName(String(id));
    const chipsRaw = (this.raw.chips as Record<string, Record<string, string>>) ?? {};
    const fwRaw = (this.raw.firmware as Record<string, string>) ?? {};
    const kit = WEAPON_LIST.map((w) => {
      const m = this.mastery[w.id];
      const rank = m?.rank ?? 1;
      const gate = m ? gateFor(w.id, m) : null;
      const into = m ? m.xp - Array.from({ length: rank - 1 }, (_, i) => xpForRank(i + 1)).reduce((a, b) => a + b, 0) : 0;
      const need = xpForRank(rank);
      const sockets = (["muzzle", "kinetic", "protocol"] as Socket[]).map((sock) => {
        const cur = chipsRaw[w.id]?.[sock] ?? "";
        const opts = CHIPS.filter((c) => c.weapon === w.id && c.socket === sock).map((c) => `<option value="${c.id}" ${c.id === cur ? "selected" : ""} ${c.rank > rank ? "disabled" : ""}>${c.name.replace(/^\S+ /, "")} · R${c.rank}${c.rank > rank ? " (LOCKED)" : ""}</option>`).join("");
        return `<label class="sock">${sock.toUpperCase()} <select data-chip="${w.id}" data-socket="${sock}"><option value="">— NONE —</option>${opts}</select></label>`;
      }).join("");
      const fws = FIRMWARES.filter((f) => f.weapon === w.id).map((f) => `<option value="${f.id}" ${fwRaw[w.id] === f.id ? "selected" : ""} ${f.rank > rank ? "disabled" : ""}>${f.name} · R${f.rank}${f.rank > rank ? " (LOCKED)" : ""}</option>`).join("");
      const chipLines = Object.values(chipsRaw[w.id] ?? {}).map((id) => chipById(id)?.line?.toUpperCase()).filter(Boolean).join(" · ");
      const fwLine = fwRaw[w.id] ? firmwareById(fwRaw[w.id]!)?.line ?? "" : "";
      const gateText = gate ? `<span class="c">GATE R${gate.gate}: ${gate.text.toUpperCase()} (${m?.counters[gate.counter] ?? 0}/${gate.need})</span>` : rank >= MAX_RANK ? `<span class="b">MASTERED</span>` : `<span class="dim">${into}/${need} XP</span>`;
      return `<div class="wk"><div class="nm">${w.name} <span class="ring">MASTERY ${String(rank).padStart(2, "0")}/${MAX_RANK}</span></div><div class="tr">${gateText}</div><div class="socks">${sockets}<label class="sock">FIRMWARE <select data-fw="${w.id}"><option value="">STOCK</option>${fws}</select></label></div>${chipLines || fwLine ? `<div class="tr">${[fwLine, chipLines].filter(Boolean).join(" · ")}</div>` : ""}</div>`;
    }).join("");
    const stampRows = STAMPS.map((st) => `<div class="st ${this.stamps.includes(st.id) ? "on" : ""}">${this.stamps.includes(st.id) ? "▣ " + st.line : "▢ " + redact(st.line)}</div>`).join("");
    const delta = v.netDelta;
    // nodes reconcile exactly; a keystone may over-pay (the Auditor banks the difference) — only an overdraft is a flag
    const stamp = delta > 1.5 ? "OVERDRAWN" : "RECONCILED";
    this.panel.innerHTML = `
      <div class="hd">▲ GHOSTFILE · <span class="cy">${v.account}</span> <span class="x" data-act="close">${closeHint("TAB", this.touchHud)}</span></div>
      <div class="ln">DEPTH <b>${String(v.depth).padStart(2, "0")}</b> · XP <b>${v.xp}</b> (${v.xpIntoDepth}/${v.xpForNext === Infinity ? "∞" : v.xpForNext}) · SCRIP <b>${v.scrip}</b> · WAKELIGHT <b>${v.wakelight}</b> · SALVAGE <b>${v.salvage}</b></div>
      <div class="ln idn">${v.identity.glyphSvg} <span class="dim">THE CITY CALLS YOU</span> <b>${v.identity.display}</b> · CHAPTER <b>${["—", "I", "II", "III"][v.identity.chapter] ?? "—"}</b>${v.identity.chapter >= 3 ? ' <span class="ye">NAMED</span>' : ""} · MONIKER <select data-moniker="1"><option value="">— NONE —</option>${MONIKERS.map((m) => `<option value="${m.id}" ${m.id === v.identity.moniker ? "selected" : ""} ${v.identity.unlocked.includes(m.id) ? "" : "disabled"}>${m.text}${v.identity.unlocked.includes(m.id) ? "" : " · " + m.how}</option>`).join("")}</select>${v.identity.debt ? ` · <span class="c">DEBT: ${v.identity.debt.display} (${filesWord(v.identity.debt.kills)} ON YOU)</span>` : ""}</div>
      <div class="ln dim">RITES ${CHAPTERS.map((c) => `${v.identity.chapters.includes(c.chapter) ? "▣" : "▢"} ${c.numeral} ${c.title} (D${c.depth})`).join(" · ")} · ${v.identity.unlocked.length}/${MONIKERS.length} MONIKERS EARNED</div>
      <div class="ln">PRIMARY <span class="wp" data-act="primary">${crewButton(wname(this.raw.primary), this.touchHud)}</span> · SECONDARY <span class="wp" data-act="secondary">${crewButton(wname(this.raw.secondary), this.touchHud)}</span> <span class="dim">(D${WEAPON_DEPTH[this.raw.primary as WeaponId] ?? "?"} / D${WEAPON_DEPTH[this.raw.secondary as WeaponId] ?? "?"})</span></div>
      <div class="cols">
        <div><div class="sh">ATTESTED NODES · ≤ ${MAX_ATTESTED} · CONNECTED <span class="dim">(${graphOpenLine(this.touchHud)})</span></div>${LEDGER_ITEMS.filter((n) => this.owned.includes(n.id) && (attested.includes(n.id) || n.ring === 1)).map(row).join("")}
          <div class="sh">KEYSTONE · ONE · LINKED</div>${KEYSTONES.map(row).join("")}</div>
        <div><div class="sh">WEAPON MASTERY · CHIPS · FIRMWARE</div><div class="kit">${kit}</div>
          <div class="sh">ATTESTATION STAMPS · ${this.stamps.length}/${STAMPS.length}</div><div class="stamps">${stampRows}</div>
          <div class="sh">LEDGER</div><div class="ledger">${v.ledger.length ? v.ledger.map((l) => `<div>${l}</div>`).join("") : "<div class='dim'>NO LINES YET</div>"}</div>
        </div>
      </div>
      <div class="eg">${this.endgameHtml()}</div>
      <div class="eg cl">${this.counterHtml()}</div>
      <div class="ft ${v.legal ? "" : "bad"}">NET DELTA: ${delta >= 0 ? "+" : "−"}${Math.abs(delta).toFixed(3)} — ${stamp} · ${v.legal ? "ATTESTATION LEGAL" : "ILLEGAL: " + v.errors.join("; ")} · ${v.applies === "now" ? "APPLIED" : "APPLIES ON NEXT LINK"}</div>
    `;
  }
}

export const ITEM_COUNT = ALL_ITEMS.length;
export const CURRICULUM_COUNT = Object.values(CURRICULA).reduce((n, c) => n + c.length, 0);
